import React, { useState, useEffect } from 'react';
import { Watch, FileBarChart2, Users2, Layers } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const FeaturesSection: React.FC = () => {
  const [visibleSection, setVisibleSection] = useState(0);
  const { t } = useLanguage();

  const features = [
    {
      id: 0,
      title: t('features.sequencer'),
      icon: <Watch className="h-10 w-10 text-primary dark:text-primary-light" />,
      description: t('features.sequencer.desc'),
      bullets: [
        t('features.sequencer.bullet1', 'Instant price and time recording'),
        t('features.sequencer.bullet2', 'No dependency on on-chain confirmations'),
        t('features.sequencer.bullet3', 'Prioritizes stop-loss/take-profit orders'),
        t('features.sequencer.bullet4', 'Minimizes slippage risks')
      ]
    },
    {
      id: 1,
      title: t('features.batch'),
      icon: <FileBarChart2 className="h-10 w-10 text-secondary dark:text-secondary-light" />,
      description: t('features.batch.desc'),
      bullets: [
        t('features.batch.bullet1', 'Priority 1: Stop-loss/Take-profit orders'),
        t('features.batch.bullet2', 'Priority 2: Market orders'),
        t('features.batch.bullet3', 'Priority 3: Limit orders'),
        t('features.batch.bullet4', 'On-chain verification for all executions')
      ]
    },
    {
      id: 2,
      title: t('features.display'),
      icon: <Layers className="h-10 w-10 text-primary dark:text-primary-light" />,
      description: t('features.display.desc'),
      bullets: [
        t('features.display.bullet1', 'Immediate position updates'),
        t('features.display.bullet2', 'Eliminates standard TON blockchain delays'),
        t('features.display.bullet3', 'Real-time portfolio tracking'),
        t('features.display.bullet4', 'Seamless user experience')
      ]
    },
    {
      id: 3,
      title: t('features.privacy'),
      icon: <Users2 className="h-10 w-10 text-secondary dark:text-secondary-light" />,
      description: t('features.privacy.desc'),
      bullets: [
        t('features.privacy.bullet1', 'Zero KYC requirements'),
        t('features.privacy.bullet2', 'Simple TON wallet connection'),
        t('features.privacy.bullet3', 'One-click trading activation'),
        t('features.privacy.bullet4', 'Complete privacy protection')
      ]
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleSection((prev) => (prev + 1) % features.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [features.length]);

  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    
    const section = document.querySelector('#features');
    if (section) observer.observe(section);
    
    return () => {
      if (section) observer.unobserve(section);
    };
  }, []);

  return (
    <section 
      id="features" 
      className="py-24 relative overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">
            {t('features.title')}
          </h2>
          <p className="max-w-3xl mx-auto text-gray-600 dark:text-gray-400 text-lg">
            {t('features.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className={`space-y-4 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            {features.map((feature) => (
              <button
                key={feature.id}
                onClick={() => setVisibleSection(feature.id)}
                className={`w-full text-left p-4 rounded-lg transition-all duration-300 ${
                  visibleSection === feature.id
                    ? 'gradient-border bg-light-card dark:bg-dark-card shadow-lg shadow-primary/10 dark:shadow-primary/5'
                    : 'hover:bg-light-card dark:hover:bg-dark-card'
                }`}
              >
                <div className="flex items-center">
                  <div className={`mr-4 transition-all duration-300 ${
                    visibleSection === feature.id ? 'text-primary dark:text-primary-light' : 'text-gray-400'
                  }`}>
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className={`font-semibold ${
                      visibleSection === feature.id ? 'text-black dark:text-white' : 'text-gray-600 dark:text-gray-300'
                    }`}>
                      {feature.title}
                    </h3>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className={`gradient-border blur-bg rounded-2xl p-6 md:p-8 transition-all duration-1000 shadow-xl shadow-primary/5 dark:shadow-primary/2 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            {features.map((feature) => (
              <div
                key={feature.id}
                className={`transition-all duration-500 ${
                  visibleSection === feature.id
                    ? 'opacity-100 block'
                    : 'opacity-0 hidden'
                }`}
              >
                <div className="flex items-center mb-6">
                  <div className="p-3 gradient-border rounded-lg mr-4 bg-light-card dark:bg-dark-card">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-black dark:text-white">{feature.title}</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                  {feature.description}
                </p>
                <ul className="space-y-3">
                  {feature.bullets.map((bullet, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="mr-2 text-primary dark:text-primary-light">•</span>
                      <span className="text-gray-600 dark:text-gray-300">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;